import React, { useEffect, useState, useMemo } from "react";
import { useAuthQuery as useQuery } from '../../shared/hooks/useAuthQuery';
import { useNavigate, Link } from "react-router-dom";
import { AdminRoutes, MainRoutes } from "../../shared/models";
import { useConvexAuth } from "convex/react";
import { api } from "../../convex/_generated/api";
import {
    Cog6ToothIcon,
    DocumentTextIcon,
    UserGroupIcon,
    ChartBarIcon,
    CheckCircleIcon,
    SparklesIcon,
    TrophyIcon,
    ChatBubbleLeftRightIcon,
    BookOpenIcon,
    CircleStackIcon,
    ShieldCheckIcon,
    ArrowUpTrayIcon,
    CpuChipIcon,
    PhotoIcon,
    WrenchScrewdriverIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    QuestionMarkCircleIcon,
    PresentationChartBarIcon,
    ClockIcon,
    ClipboardDocumentListIcon
} from "@heroicons/react/24/outline";
import { AcornIcon } from "../../shared/components/AcornIcon";

const adminSections = [
    {
        title: 'Users & Analytics',
        description: 'Monitor platform usage',
        color: 'from-green-50 to-green-100/50',
        borderColor: 'border-green-200',
        items: [
            { label: 'All Users', icon: UserGroupIcon, route: AdminRoutes.Users, description: 'View user accounts' },
            { label: 'User Growth & Retention', icon: ChartBarIcon, route: AdminRoutes.UserAnalytics, description: 'Total vs active users, retention metrics' },
            { label: 'Subscribers', icon: SparklesIcon, route: AdminRoutes.Subscribers, description: 'Manage subscriptions' },
            { label: 'Session Analytics', icon: ChartBarIcon, route: AdminRoutes.Sessions, description: 'Usage statistics' },
            { label: 'User Progress', icon: ChartBarIcon, route: AdminRoutes.UserProgress, description: 'Charts and analytics per user' },
            { label: 'User Feedback', icon: ChatBubbleLeftRightIcon, route: AdminRoutes.Feedback, description: 'Review user feedback' },
            { label: 'Engagement Analytics', icon: PresentationChartBarIcon, route: AdminRoutes.EngagementAnalytics, description: 'Onboarding, engagement, retention & economy' },
            { label: 'Screen Time', icon: ClockIcon, route: AdminRoutes.ScreenTimeAnalytics, description: 'Active & idle time per page' },
            { label: 'Audit Log', icon: ClipboardDocumentListIcon, route: AdminRoutes.AuditLog, description: 'Track all admin actions' },
        ]
    },
    {
        title: 'Assistant Management',
        description: 'Configure your AI assistants',
        color: 'from-blue-50 to-blue-100/50',
        borderColor: 'border-blue-200',
        items: [
            { label: 'Manage Assistants', icon: ChatBubbleLeftRightIcon, route: AdminRoutes.Assistants, description: 'View and edit all AI assistants' },
            { label: 'Assistant Types', icon: SparklesIcon, route: 'types', description: 'Configure assistant categories' },
            { label: 'Assistant Metadata', icon: CircleStackIcon, route: AdminRoutes.AssistantMetadata, description: 'Manage assistant properties' },
        ]
    },
    {
        title: 'System Configuration',
        description: 'Core platform settings',
        color: 'from-primary/5 to-primary/10',
        borderColor: 'border-primary/30',
        items: [
            { label: 'Skills & Levels', icon: TrophyIcon, route: AdminRoutes.Skills, description: 'Manage skill trees and points' },
            { label: 'Points Configuration', icon: ChartBarIcon, route: AdminRoutes.LevelConfig, description: 'Configure points and levels' },
            { label: 'Acorn System', icon: AcornIcon, route: AdminRoutes.AcornConfig, description: 'Configure currency & rewards', highlight: true },
            { label: 'Progress Tracking', icon: ChartBarIcon, route: AdminRoutes.Progress, description: 'Define milestones' },
            { label: 'System Assistants', icon: Cog6ToothIcon, route: AdminRoutes.System, description: 'Core AI configurations' },
            { label: 'Documentation', icon: BookOpenIcon, route: AdminRoutes.Documentation, description: 'Help content & guides' },
            { label: 'Knowledge Base', icon: CpuChipIcon, route: AdminRoutes.KnowledgeBase, description: 'Training data & context' },
            { label: 'AI Tools', icon: WrenchScrewdriverIcon, route: AdminRoutes.Tools, description: 'Manage AI tool definitions' },
            { label: 'Inner Weather Quotes', icon: ChatBubbleLeftRightIcon, route: AdminRoutes.InnerWeatherQuotes, description: 'Manage quotes for inner weather states' },
            { label: 'Legal & Compliance', icon: ShieldCheckIcon, route: AdminRoutes.Legal, description: 'Terms & disclaimers' },
        ]
    },
    {
        title: 'Context & Content',
        description: 'Manage AI knowledge',
        color: 'from-purple-50 to-purple-100/50',
        borderColor: 'border-purple-200',
        items: [
            { label: 'Global Context', icon: DocumentTextIcon, route: AdminRoutes.Context, description: 'Platform-wide AI context' },
            { label: 'Upload Manual Context', icon: ArrowUpTrayIcon, route: AdminRoutes.ManualContextUpload, description: 'Add custom knowledge' },
            { label: 'Embeddings Config', icon: CircleStackIcon, route: AdminRoutes.EmbeddingsConfig, description: 'Vector database settings' },
        ]
    },
    {
        title: 'Onboarding & Engagement',
        description: 'User onboarding and engagement tools',
        color: 'from-orange-50 to-orange-100/50',
        borderColor: 'border-orange-200',
        items: [
            { label: 'Intro Slides', icon: PhotoIcon, route: AdminRoutes.IntroSlides, description: 'Pre-onboarding intro slides' },
            { label: 'Manage Check-ins', icon: CheckCircleIcon, route: AdminRoutes.CheckinInstructions, description: 'User reflection prompts' },
            { label: 'FAQ', icon: QuestionMarkCircleIcon, route: AdminRoutes.FAQ, description: 'Manage user-facing FAQs' },
        ]
    },
];

// Flatten all section titles for category filter chips
const sectionTitles = adminSections.map(s => s.title);

export const Admin: React.FC = () => {
    const { isLoading, isAuthenticated } = useConvexAuth();
    const navigate = useNavigate();
    const userStats = useQuery(api.admin_analytics.getUserStats);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeCategory, setActiveCategory] = useState<string | null>(null);

    useEffect(() => {
        if (!isAuthenticated) {
            navigate(MainRoutes.Landing);
        }
    }, [isAuthenticated]);

    const filteredSections = useMemo(() => {
        const q = searchTerm.toLowerCase().trim();

        return adminSections
            .filter((section) => {
                // Category filter
                if (activeCategory && section.title !== activeCategory) return false;
                return true;
            })
            .map((section) => {
                if (!q) return section;
                // Text search: filter items within each section
                const matchedItems = section.items.filter(
                    (item) =>
                        item.label.toLowerCase().includes(q) ||
                        item.description.toLowerCase().includes(q)
                );
                return { ...section, items: matchedItems };
            })
            .filter((section) => section.items.length > 0);
    }, [searchTerm, activeCategory]);

    const hasActiveFilters = searchTerm.length > 0 || activeCategory !== null;

    const clearFilters = () => {
        setSearchTerm("");
        setActiveCategory(null);
    };

    return (
        <div className="min-h-screen bg-background pt-24">
            {/* Header */}
            <div className="border-b border-border/50 bg-gradient-to-r from-primary/5 to-accent/5 px-6 py-8">
                <div>
                    <div className="flex items-center gap-4 mb-2">
                        <img src="/logo.png" alt="Logo" className="w-32 object-contain" />
                        <div>
                            <h1 className="text-3xl font-semibold text-foreground mb-1">Admin Dashboard</h1>
                            <p className="text-base text-muted-foreground">
                                Manage your platform and help users on their journey
                            </p>
                        </div>
                    </div>

                    {/* User Stats Cards */}
                    {userStats && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                            {/* Total Users Card */}
                            <div
                                onClick={() => navigate(AdminRoutes.UserAnalytics)}
                                className="cursor-pointer border-2 border-border/50 bg-card hover:shadow-lg transition-all rounded-xl p-5"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-primary/10">
                                            <UserGroupIcon className="h-5 w-5 text-primary" />
                                        </div>
                                        <span className="text-sm font-medium text-muted-foreground">Total Users</span>
                                    </div>
                                    <span className="text-2xl font-bold text-foreground">{userStats.totalUsers}</span>
                                </div>
                                <div className="flex gap-3 flex-wrap">
                                    {([1, 7, 14] as const).map(days => {
                                        const val = userStats.trends[days]?.total || 0;
                                        return (
                                            <div key={days} className="flex items-center gap-1 text-xs">
                                                <span className="text-muted-foreground">{days}d:</span>
                                                <span className={val > 0 ? "text-green-600 font-medium" : "text-muted-foreground"}>
                                                    +{val}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Active Users Card */}
                            <div
                                onClick={() => navigate(AdminRoutes.UserAnalytics)}
                                className="cursor-pointer border-2 border-border/50 bg-card hover:shadow-lg transition-all rounded-xl p-5"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 rounded-lg bg-green-500/10">
                                            <ChartBarIcon className="h-5 w-5 text-green-600" />
                                        </div>
                                        <span className="text-sm font-medium text-muted-foreground">Active Users</span>
                                    </div>
                                    <span className="text-2xl font-bold text-foreground">{userStats.activeUsers}</span>
                                </div>
                                <div className="flex gap-3 flex-wrap">
                                    {([1, 7, 14] as const).map(days => {
                                        const val = userStats.trends[days]?.active || 0;
                                        return (
                                            <div key={days} className="flex items-center gap-1 text-xs">
                                                <span className="text-muted-foreground">{days}d:</span>
                                                <span className={val > 0 ? "text-green-600 font-medium" : "text-muted-foreground"}>
                                                    {val}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-8">
                <div className="space-y-8">
                    {/* Filter Bar */}
                    <div className="border-2 border-border/50 bg-card p-4 rounded-xl space-y-3">
                        {/* Search */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1 relative">
                                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search admin pages..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50"
                                />
                            </div>
                            {hasActiveFilters && (
                                <button
                                    onClick={clearFilters}
                                    className="flex items-center gap-1 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                    Clear
                                </button>
                            )}
                        </div>

                        {/* Category Chips */}
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setActiveCategory(null)}
                                className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                                    activeCategory === null
                                        ? 'bg-primary text-white border-primary'
                                        : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                                }`}
                            >
                                All
                            </button>
                            {sectionTitles.map((title) => (
                                <button
                                    key={title}
                                    onClick={() => setActiveCategory(activeCategory === title ? null : title)}
                                    className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                                        activeCategory === title
                                            ? 'bg-primary text-white border-primary'
                                            : 'bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                                    }`}
                                >
                                    {title}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Sections */}
                    {filteredSections.map((section) => (
                        <div key={section.title} className="space-y-4">
                            <div>
                                <h2 className="text-2xl font-semibold text-foreground mb-1">{section.title}</h2>
                                <p className="text-base text-muted-foreground">{section.description}</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {section.items.map((item) => {
                                    const Icon = typeof item.icon === 'string' ? null : item.icon;

                                    return (
                                        <Link key={item.label} to={item.route}>
                                            <div className={`border-2 ${section.borderColor} bg-gradient-to-br ${section.color} hover:shadow-lg transition-all cursor-pointer h-full rounded-xl`}>
                                                <div className="p-6">
                                                    <div className="flex items-start gap-4">
                                                        <div className="p-3 rounded-xl bg-white border-2 border-border/50 flex-shrink-0">
                                                            {Icon ? (
                                                                <Icon className="h-6 w-6 text-primary" />
                                                            ) : (
                                                                <span className="text-2xl">{item.icon}</span>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <h3 className="text-lg font-medium text-foreground">
                                                                    {item.label}
                                                                </h3>
                                                                {item.highlight && (
                                                                    <SparklesIcon className="h-4 w-4 text-primary" />
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                                {item.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}

                    {/* No Results */}
                    {filteredSections.length === 0 && hasActiveFilters && (
                        <div className="text-center py-12 bg-muted/30 rounded-xl">
                            <p className="text-muted-foreground text-lg mb-4">
                                No pages match your filter
                            </p>
                            <button
                                onClick={clearFilters}
                                className="px-4 py-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                            >
                                Clear filters
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}